import { IPlatformAPI } from '@wix/native-components-infra/dist/src/types/types';
import {
  type BaseCurrentCartServiceProps,
  type ICurrentCartService,
  AddToCurrentCartV1Options,
  Cart,
  CartV1,
  CreateCheckoutFromCurrentCartOptions,
  CreateCheckoutFromCurrentCartResponse,
  EstimateCurrentCartTotals,
  EstimateCurrentCartTotalsOptions,
} from './types';
import * as currentCart from '@wix/auto_sdk_ecom_current-cart-v-2';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { EcomPlatformViewerScriptContext } from '../../viewer-script/types';

export abstract class BaseCurrentCartService implements ICurrentCartService {
  protected readonly apis: IPlatformAPI;
  protected readonly siteStore: SiteStore;
  private readonly callbacks: ((options?: { revision?: string }) => void)[] =
    [];
  protected readonly context: EcomPlatformViewerScriptContext;
  protected constructor({ context, apis }: BaseCurrentCartServiceProps) {
    this.apis = apis;
    this.siteStore = context.siteStore;
    this.context = context;
  }
  abstract getCurrentCart(): Promise<Cart>;
  abstract getCurrentCartV1(): Promise<CartV1>;
  abstract calculateCurrentCart(
    options?: currentCart.CalculateCurrentCartRequest,
  ): Promise<currentCart.CalculateCurrentCartResponse>;
  abstract estimateCurrentCart(
    options?: currentCart.EstimateCurrentCartRequest,
  ): Promise<currentCart.EstimateCurrentCartResponse>;
  abstract estimateCurrentCartTotalsV1(
    options?: EstimateCurrentCartTotalsOptions,
  ): Promise<EstimateCurrentCartTotals>;
  abstract init(): void;
  abstract invalidateAndReload(): Promise<void>;
  abstract onLoginStart(): Promise<void>;
  abstract onLoginFinish(): Promise<void>;
  abstract updateLineItemsInCurrentCart(
    options: currentCart.UpdateLineItemsInCurrentCartOptions,
  ): Promise<Cart>;
  abstract updateLineItemQuantityV1(
    options: { lineItemId: string; quantity: number },
    biProps: { origin?: string },
  ): Promise<void>;
  abstract removeLineItemsFromCurrentCart({
    lineItemIds,
  }: {
    lineItemIds: string[];
  }): Promise<Cart>;
  abstract removeLineItemV1(
    options: { lineItemId: string },
    biProps?: { origin?: string },
  ): Promise<void>;
  abstract updateBuyerNoteV1(
    { buyerNote }: { buyerNote: string },
    biProps?: { origin?: string },
  ): Promise<void>;
  abstract addCouponToCurrentCart({
    coupon,
  }: {
    coupon: currentCart.CouponInput;
  }): Promise<Cart>;
  abstract applyCouponV1(
    { code }: { code: string },
    biProps?: { origin?: string },
  ): Promise<void>;
  abstract removeCouponFromCurrentCart({
    couponId,
  }: {
    couponId: string;
  }): Promise<Cart>;
  abstract removeCouponV1(biProps?: { origin?: string }): Promise<void>;
  abstract setAddressV1(payload: {
    address: { country: string; subdivision?: string; zipCode?: string };
  }): Promise<void>;
  abstract setDeliveryMethodForCurrentCart({
    deliveryMethod,
  }: {
    deliveryMethod: currentCart.DeliveryMethodInput;
  }): Promise<Cart>;
  abstract removeDeliveryMethodFromCurrentCart(): Promise<Cart>;
  abstract selectShippingOptionV1(payload: {
    selectedShippingOption?: { optionId: string; shippingRuleId: string };
    option?: { code: string; carrierId: string };
  }): Promise<void>;
  abstract addLineItemsToCurrentCart(
    options: currentCart.AddLineItemsToCurrentCartOptions,
  ): Promise<Cart>;
  abstract addToCurrentCartV1(
    options: AddToCurrentCartV1Options,
  ): Promise<void>;
  abstract updateCurrentCart(cart: Cart): Promise<Cart>;
  abstract refreshCurrentCart(): Promise<Cart>;
  abstract removeGiftCardFromCurrentCart({
    giftCardId,
  }: {
    giftCardId: string;
  }): Promise<Cart>;
  abstract addGiftCardToCurrentCart({
    giftCardInput,
  }: {
    giftCardInput: currentCart.GiftCardInput;
  }): Promise<Cart>;
  abstract createCheckoutV1(
    options: CreateCheckoutFromCurrentCartOptions,
  ): Promise<CreateCheckoutFromCurrentCartResponse>;
  abstract getCurrentCartIdFromCache(): Promise<string | undefined>;
  abstract updateFormSubmissionsInCurrentCart(
    options: currentCart.UpdateFormSubmissionsInCurrentCartOptions,
  ): Promise<Cart>;

  public onChange(
    callback: (options?: { revision?: string }) => void | Promise<void>,
  ) {
    this.callbacks.push(callback);
  }

  protected runCallbacks(options?: { revision?: string }) {
    this.callbacks.forEach((callback) => callback(options));
  }
}
